import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import {
	COLLECTION_OR_FOLDER_OPTIONS,
	getMaterialRequisitionRequestBody,
	isMaterialRequisitionFormValid,
	MATERIAL_REQUISITION_PURPOSE,
	MATERIAL_REQUISITION_TYPE
} from '@features/material-requisition/utils';
import {
	useAddMaterialRequisitionMutation,
	useFetchFabricTypeListQuery,
	useFetchTrimsAndAccessoriesQuery,
	useLazyFetchBrandListQuery,
	useLazyFetchCollectionListQuery,
	useLazyFetchMyFolderListQuery
} from '@features/material-requisition/api/materialRequisitionApi';
import MaterialColorWrapper from '@features/material-requisition/components/MaterialColorWrapper';
import Composition from '@features/material-requisition/components/Composition';
import { renderAsyncDropdown, renderDropdown } from '@features/material-requisition/components/index';

import {
	adjustTextAreaHeight,
	capitalizeFirstLetter,
	generateRandomString,
	validateNumber
} from '@utils/Util';

const MaterialRequisitionModal = ({ selectedMaterialType, goBack, onCloseModal }) => {
	const { readFile } = useSingleFileUpload();
	const [files, setFiles] = useState([]);
	const [selectedCollectionOrFolder, setSelectedCollectionOrFolder] = useState({});
	const [getCollectionList] = useLazyFetchCollectionListQuery();
	const [getMyFolderList] = useLazyFetchMyFolderListQuery();
	const [getBrandList] = useLazyFetchBrandListQuery();
	const { data: fabricTypes } = useFetchFabricTypeListQuery();
	const { data: trimsAndAccessoriesTypes } = useFetchTrimsAndAccessoriesQuery(
		{
			materialType: selectedMaterialType.materialType
		},
		{ skip: !['TRIMS', 'ACCESSORIES'].includes(selectedMaterialType.materialType) }
	);
	const [addMaterialRequisition, { isLoading }] = useAddMaterialRequisitionMutation();
	const [selectedCollectionOrFolderList, setSelectedCollectionOrFolderList] = useState([]);
	const [selectedBrandList, setSelectedBrandList] = useState([]);
	const [selectedMaterialTypeList, setSelectedMaterialTypeList] = useState([]);
	const [selectedTrimsAndAccessoriesList, setSelectedTrimsAndAccessoriesList] = useState({});
	const [materials, setMaterials] = useState([
		{
			uId: generateRandomString(),
			colorSections: [
				{
					id: generateRandomString(),
					selectedGroupColorList: [],
					selectedPantoneColorList: [],
					pantoneColorIds: [],
					colorGroupIdList: [],
					colorType: 'SOLID',
					isMultiSelect: false
				}
			]
		}
	]);
	const [inputData, setInputData] = useState({ requiredDate: '', description: '', price: '' });
	const [errors, setErrors] = useState({});
	const [colorSections, setColorSections] = useState([
		{
			id: generateRandomString(),
			selectedGroupColorList: [],
			selectedPantoneColorList: [],
			pantoneColorIds: [],
			colorGroupIdList: [],
			colorType: 'SOLID',
			isMultiSelect: false
		}
	]);
	const navigate = useNavigate();

	const handleTabChange = (tabName, item) => {
		const selectedColor = colorSections.map((color) => {
			if (color.id === item.id) {
				return {
					...color,
					selectedGroupColorList: [],
					selectedPantoneColorList: [],
					colorType: tabName,
					isMultiSelect: tabName !== 'SOLID'
				};
			}
			return color;
		});
		setColorSections(selectedColor);
	};

	const handleColorGroupIdList = (items, id, fieldName) => {
		const selectedColor = colorSections.map((color) => {
			if (color.id === id) {
				return {
					...color,
					[fieldName]: items
				};
			}
			return color;
		});
		setColorSections(selectedColor);
	};

	const renderFabricType = () => {
		if (['FABRIC', 'SWATCH'].includes(selectedMaterialType.materialType)) {
			return renderAsyncDropdown(
				'Fabric Type *',
				fabricPromiseOptions,
				(items) => setSelectedMaterialTypeList(items),
				selectedMaterialTypeList,
				'Select',
				true,
				fabricTypes,
				errors?.fabricTypeError
			);
		}
	};

	const renderTrimsAndAccessories = () => {
		if (['TRIMS', 'ACCESSORIES'].includes(selectedMaterialType.materialType)) {
			return (
				<>
					{renderDropdown(
						`${capitalizeFirstLetter(selectedMaterialType.materialType)} *`,
						trimsAndAccessoriesTypes,
						selectedTrimsAndAccessoriesList.value,
						(item) => {
							setSelectedTrimsAndAccessoriesList(item);
						},
						errors?.trimsAndAccessoriesError
					)}
					<div className="col-md-6">
						<div className="form-group required-quantity-group">
							<label htmlFor="collection">Required quantity *</label>
							<input
								type="number"
								placeholder="Quantity"
								name="quantity"
								onChange={handleChange}
								onKeyPress={validateNumber}
								value={inputData.quantity}
							/>
							{errors?.quantityError && <p className="error d-block">{errors.quantityError}</p>}
						</div>
					</div>
				</>
			);
		}
	};

	const renderAddColor = () => {
		return (
			<div className="col-md-6">
				<MaterialColorWrapper
					colorSections={colorSections}
					handleTabChange={handleTabChange}
					handleColorGroupIdList={handleColorGroupIdList}
					setColorSections={setColorSections}
					isMultiSelection={
						MATERIAL_REQUISITION_PURPOSE[0].constant ===
							selectedMaterialType.requisitionPurpose &&
						MATERIAL_REQUISITION_TYPE[0].constant === selectedMaterialType.materialType
					}
				/>
				{errors?.groupColorError && <p className="error d-block">{errors.groupColorError}</p>}
				{errors?.pantoneColorError && <p className="error d-block">{errors.pantoneColorError}</p>}
			</div>
		);
	};

	const renderBrandField = (className) => {
		return renderAsyncDropdown(
			'Brand *',
			brandPromiseOptions,
			(items) => setSelectedBrandList(items),
			selectedBrandList,
			'Select',
			false,
			true,
			errors?.brandError,
			className
		);
	};

	const promiseOptions = (inputValue) => {
		return new Promise(async (resolve) => {
			const fetchFunction =
				selectedCollectionOrFolder?.id === 'COLLECTION' ? getCollectionList : getMyFolderList;
			const data = await fetchFunction({ search: inputValue }).unwrap();
			resolve(data);
		});
	};

	const brandPromiseOptions = (inputValue) => {
		return new Promise(async (resolve) => {
			const data = await getBrandList({ name: inputValue }).unwrap();
			resolve(data);
		});
	};

	const fabricPromiseOptions = (inputValue) => {
		return new Promise(async (resolve) => {
			const data = fabricTypes?.filter((item) =>
				item.label.toLowerCase().includes(inputValue.toLowerCase())
			);
			resolve(data);
		});
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setInputData((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const renderDescription = () => {
		return (
			<div className="col-md-12">
				<div className="form-group">
					<label htmlFor="collection">Description</label>
					<textarea
						value={inputData.description}
						placeholder="Description"
						rows={3}
						onInput={(e) => adjustTextAreaHeight(e, '84px')}
						name="description"
						onChange={handleChange}
					/>
				</div>
			</div>
		);
	};

	const isSwatchSelected = selectedMaterialType.materialType === MATERIAL_REQUISITION_TYPE[1].constant;

	const renderNitexDevelopmentFields = () => {
		if (
			!isSwatchSelected &&
			[MATERIAL_REQUISITION_PURPOSE[0].constant, MATERIAL_REQUISITION_PURPOSE[2].constant].includes(
				selectedMaterialType.requisitionPurpose
			)
		) {
			return (
				<>
					{renderDropdown(
						'Select collection / folder *',
						COLLECTION_OR_FOLDER_OPTIONS,
						selectedCollectionOrFolder.value,
						(item) => {
							setSelectedCollectionOrFolderList([]);
							setSelectedCollectionOrFolder(item);
						},
						errors?.collectionOrFolderTypeError
					)}
					{selectedCollectionOrFolder?.id === 'COLLECTION' &&
						renderAsyncDropdown(
							'Select Collection *',
							promiseOptions,
							(items) => {
								setSelectedCollectionOrFolderList(items);
								setSelectedBrandList({ label: items.brandName, value: items.brandId });
							},
							selectedCollectionOrFolderList,
							'Select',
							false,
							true,
							errors?.collectionOrFolderError
						)}
					{selectedCollectionOrFolder?.id === 'FOLDER' &&
						renderAsyncDropdown(
							'Select Folder',
							promiseOptions,
							(items) => {
								setSelectedCollectionOrFolderList(items);
								setSelectedBrandList({ label: items.brandName, value: items.brandId });
							},
							selectedCollectionOrFolderList,
							'Select',
							false,
							true,
							errors?.collectionOrFolderError
						)}
					{renderBrandField('col-md-6')}
					{renderRequiredDate()}
					{renderFabricType()}
					{renderTrimsAndAccessories()}
					{renderAddColor()}
					{renderDescription()}
				</>
			);
		}
	};

	const renderRequiredDate = () => {
		return (
			<div className="col-md-6">
				<div className="form-group">
					<label htmlFor="collection">Request needs to be served within*</label>
					<input
						type="date"
						name="requiredDate"
						onChange={handleChange}
						min={new Date().toISOString().split('T')[0]}
						value={inputData.requiredDate}
					/>
					{errors?.requiredDateError && <p className="error d-block">{errors.requiredDateError}</p>}
				</div>
			</div>
		);
	};

	const renderBuyerRequestFields = () => {
		if (
			selectedMaterialType.requisitionPurpose === MATERIAL_REQUISITION_PURPOSE[1].constant ||
			isSwatchSelected
		) {
			return (
				<>
					{renderRequiredDate()}
					{renderBrandField('col-md-6')}
				</>
			);
		}
	};

	const handleChangeBuyerRequest = (e, item) => {
		const { name, value } = e.target;
		const updatedMaterials = materials.map((mat) =>
			mat.uId === item.uId ? { ...mat, [name]: value } : mat
		);
		setMaterials(updatedMaterials);
	};

	const handleDropDownChangeBuyerRequest = (selected, item, fieldName) => {
		const updatedMaterials = materials.map((mat) =>
			mat.uId === item.uId ? { ...mat, [fieldName]: selected } : mat
		);
		setMaterials(updatedMaterials);
	};

	const handleComposition = (compositions, item) => {
		const updatedMaterials = materials.map((mat) =>
			mat.uId === item.uId ? { ...mat, compositions: compositions } : mat
		);
		setMaterials(updatedMaterials);
	};

	const renderWeightAndComposition = () => {
		const handleColorTabChange = (tabName, selectedColor, selectedMaterial) => {
			setMaterials((prevMaterials) =>
				prevMaterials.map((material) => {
					if (material.uId === selectedMaterial.uId) {
						return {
							...material,
							colorSections: material.colorSections.map((color) => {
								if (color.id === selectedColor.id) {
									return {
										...color,
										selectedGroupColorList: [],
										selectedPantoneColorList: [],
										colorType: tabName,
										isMultiSelect: tabName !== 'SOLID'
									};
								}
								return color;
							})
						};
					}
					return material;
				})
			);
		};

		const handleGroupIdList = (items, id, fieldName, selectedMaterial) => {
			setMaterials((prevMaterials) =>
				prevMaterials.map((material) => {
					if (material.uId === selectedMaterial.uId) {
						return {
							...material,
							colorSections: material.colorSections.map((color) => {
								if (color.id === id) {
									return {
										...color,
										[fieldName]: items
									};
								}
								return color;
							})
						};
					}
					return material;
				})
			);
		};

		const handleRemove = (item) => {
			const updatedMaterials = materials.filter((mat) => mat.uId !== item.uId);
			setMaterials(updatedMaterials);
		};

		return materials.map((item, index) => (
			<div className="row repeat-row position-relative" key={`material_${item.uId}`}>
				{materials.length > 1 && (
					<span className="action-icon cursor-pointer">
						<CommonImage
							src="/icons/delete_gray.svg"
							width={20}
							onClick={() => handleRemove(item)}
						/>
					</span>
				)}
				{renderAsyncDropdown(
					'Fabric Type *',
					fabricPromiseOptions,
					(items) => handleDropDownChangeBuyerRequest(items, item, 'materialTypeList'),
					item?.materialTypeList || [],
					'Select',
					false,
					fabricTypes,
					errors?.[`fabricType_${item.uId}`]
				)}
				<div className="col-md-6">
					<div className="form-group position-relative">
						<label>GSM*</label>
						<div className="d-flex">
							<input
								className="input-percentage w-100"
								max={100}
								min="0.1"
								type="number"
								name="weight"
								value={item.weight || ''}
								onChange={(e) => handleChangeBuyerRequest(e, item)}
							/>
						</div>
						{errors?.[`weight_${item.uId}`] && (
							<p className="error d-block">{errors?.[`weight_${item.uId}`]}</p>
						)}
						{errors?.[`weight_type_${item.uId}`] && (
							<p className="error d-block">{errors?.[`weight_type_${item.uId}`]}</p>
						)}
					</div>
				</div>
				<Composition
					addCompositions={(compositions) => handleComposition(compositions, item)}
					errors={errors}
					materialIndex={index}
				/>
				{selectedMaterialType.materialType === MATERIAL_REQUISITION_TYPE[0].constant && (
					<div className="col-md-6">
						<div className="form-group required-quantity-group">
							<label htmlFor="collection">Required quantity *</label>
							<div className="d-flex">
								<input
									type="number"
									placeholder="Quantity"
									name="quantity"
									onKeyPress={validateNumber}
									value={item.quantity || ''}
									onChange={(e) => handleChangeBuyerRequest(e, item)}
								/>
								<div className="select-component">
									<div className="form-group size36 mb-0">
										<div className="select-component">
											<div className="click-box click mb-1">
												<p className="regular-14 pl-1">
													{item?.materialTypeList?.label?.startsWith('Knit')
														? 'Kg'
														: item?.materialTypeList?.label
															? 'Yard'
															: ''}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
							{errors?.[`quantity_${item.uId}`] && (
								<p className="error d-block">{errors?.[`quantity_${item.uId}`]}</p>
							)}
						</div>
					</div>
				)}
				<div className="col-md-6">
					<MaterialColorWrapper
						colorSections={item.colorSections}
						handleTabChange={(tabName, selectedColor) =>
							handleColorTabChange(tabName, selectedColor, item)
						}
						handleColorGroupIdList={(items, id, fieldName) =>
							handleGroupIdList(items, id, fieldName, item)
						}
						setColorSections={setColorSections}
					/>
					{errors?.[`groupColorError_${item.uId}`] && (
						<p className="error d-block">{errors?.[`groupColorError_${item.uId}`]}</p>
					)}
					{errors?.[`pantoneColorError_${item.uId}`] && (
						<p className="error d-block">{errors?.[`pantoneColorError_${item.uId}`]}</p>
					)}
				</div>
			</div>
		));
	};

	const renderBuyerRequestDynamicFields = () => {
		if (
			isSwatchSelected ||
			(selectedMaterialType.requisitionPurpose === MATERIAL_REQUISITION_PURPOSE[1].constant &&
				[MATERIAL_REQUISITION_TYPE[0].constant, MATERIAL_REQUISITION_TYPE[1].constant].includes(
					selectedMaterialType.materialType
				))
		) {
			return (
				<>
					{renderWeightAndComposition()}
					<div className="add-new-design mb-12 text-right">
						<button
							className="button text size32 px-0"
							onClick={() =>
								setMaterials([
									...materials,
									{
										uId: generateRandomString(),
										colorSections: [
											{
												id: generateRandomString(),
												selectedGroupColorList: [],
												selectedPantoneColorList: [],
												pantoneColorIds: [],
												colorGroupIdList: [],
												colorType: 'SOLID',
												isMultiSelect: false
											}
										]
									}
								])
							}
						>
							<CommonImage src="/icons/add-round.svg" alt="add" />
							<span className="ml-1">Add</span>
						</button>
					</div>
					<div className="row">{renderDescription()}</div>
				</>
			);
		}
	};

	const handleFileChange = async (e) => {
		const files = e.target.files;
		if (files.length > 0) {
			let fileList = [];
			for (const file of files) {
				const fileStr = await readFile(file);
				fileList.push({
					name: file.name,
					base64Str: fileStr,
					docMimeType: file.type,
					documentType: 'MATERIAL_REQUISITION'
				});
			}
			setFiles(fileList);
		}
	};

	const removeFile = (index) => {
		setFiles(files.filter((file, i) => i !== index));
	};

	const isValid = () => {
		let errorObj = isMaterialRequisitionFormValid(
			selectedMaterialType,
			selectedCollectionOrFolder,
			selectedCollectionOrFolderList,
			inputData,
			selectedMaterialTypeList,
			selectedTrimsAndAccessoriesList,
			materials,
			colorSections,
			selectedBrandList
		);
		setErrors(errorObj);
		return Object.keys(errorObj).length === 0;
	};

	const handleSubmit = async () => {
		if (isValid()) {
			try {
				let requestBody = getMaterialRequisitionRequestBody(
					inputData,
					selectedMaterialType,
					selectedCollectionOrFolder,
					selectedCollectionOrFolderList,
					selectedBrandList,
					selectedTrimsAndAccessoriesList,
					selectedMaterialTypeList,
					files,
					colorSections,
					materials
				);
				const data = await addMaterialRequisition({ requestBody }).unwrap();
				toastSuccess(
					`Material requisition created with reference number: ${data?.payload?.referenceId}`
				);
				navigate(`/materials/requisition?activeTab=${selectedMaterialType.materialType}`);
				onCloseModal();
			} catch {
				toastError('Failed to add material requisition');
			}
		}
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="common-popup-body body-scroll pb-0">
				<div className="row">
					<div className="col-md-12 pr-2">
						<div className="upload-img mb-2">
							<div className="file-uploader small upload-design-image mb-4 mb-sm-0">
								<label className="drag-upload" htmlFor="drag-upload" />
								<input
									className="file-upload"
									name="drag-upload"
									id="drag-upload"
									type="file"
									multiple={true}
									onChange={handleFileChange}
								/>
							</div>
						</div>
					</div>
					<div className="col-md-12 pr-2 mb-2 mt-1">
						{files?.map((file, index) => (
							<span key={index} className="regular-14 mr-2">
								{file.name}
								<CommonImage
									src="/icons/delete24.svg"
									alt="icon-remove"
									onClick={() => removeFile(index)}
									className="cursor-pointer ml-1"
									width={16}
									height={16}
								/>
							</span>
						))}
					</div>
					{renderNitexDevelopmentFields()}
					{renderBuyerRequestFields()}
				</div>
				{renderBuyerRequestDynamicFields()}
			</div>
			<div className="modal-footer">
				<div className="footer-buttons text-right">
					<button className="button outline size32 ml-2" onClick={goBack}>
						Back
					</button>
					<button className="button size32 ml-2" onClick={handleSubmit}>
						Submit
					</button>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default MaterialRequisitionModal;
